import { Buffer, Channel, SagaIterator, buffers } from 'redux-saga';
import { ActionPattern, actionChannel, all, call, delay, flush, fork, put, select, take } from 'redux-saga/effects';
import { IRepoState } from 'redux-state/rootReducer.js';
import { IWarningPayloadData, WARNINGS_EXPIRY_PERIOD } from 'redux-state/weather-warnings/types.js';
import { makeRequest } from 'utilities/fetch/api-gateway.js';
import {
  REDUX_WEATHER_WARNINGS_FETCH_ERROR,
  REDUX_WEATHER_WARNINGS_FETCH_EXPIRED,
  REDUX_WEATHER_WARNINGS_FETCH_INIT,
  REDUX_WEATHER_WARNINGS_FETCH_RETRY,
  REDUX_WEATHER_WARNINGS_FETCH_SUCCESS,
  WEATHER_WARNINGS_FETCH_TYPE,
  weatherWarningsFetchTypes,
} from './reducer.js';
import { WarningRequestArray } from './warningRequest.js';

const FAIL_RETRY_TIMEOUT = 1000 * 20; // 20 secs

/**
 * Fetches weather warnings json
 * 1) Checks if has location, if not send message warningLoadingStateFailed
 * 2)
 * 2) Make request for weather data
 * 3) If fetch fails
 *  - send message warningLoadingStateFailed
 *  - delay
 *  - check message warning location fits page location
 *  - send refetch request
 * 4_ If fetch succeeds send success and wait for delay before requesting again
 */

function* updateWarningsForLocation(action: WEATHER_WARNINGS_FETCH_TYPE) {
  const { warningProps: locationRequest } = action.payload;
  if (!locationRequest || !locationRequest.key || !locationRequest.type) {
    console.error('No warning location request');
    yield put(REDUX_WEATHER_WARNINGS_FETCH_ERROR({ locationRequest: locationRequest, data: { warnings: [] } }));
  } else {
    try {
      const response: IWarningPayloadData = yield call(makeRequest, {
        path: `/warnings-service/${locationRequest.type}/${locationRequest.key}`,
        apiPrefix: true,
      });
      yield put(
        REDUX_WEATHER_WARNINGS_FETCH_SUCCESS({
          locationRequest: locationRequest,
          data: response,
        }),
      );
      yield delay(WARNINGS_EXPIRY_PERIOD);
      yield put(REDUX_WEATHER_WARNINGS_FETCH_EXPIRED(action.payload));
    } catch (e) {
      console.error('Fetch Error', e);
      yield put(REDUX_WEATHER_WARNINGS_FETCH_ERROR({ locationRequest: locationRequest, data: { warnings: [] } }));
      yield delay(FAIL_RETRY_TIMEOUT);
      yield put(REDUX_WEATHER_WARNINGS_FETCH_RETRY(action.payload));
    }
  }
}

export interface Action<T extends string = string> {
  type: T;
}
/**
 * Catch all Fetch_Init, Fetch_Retry, Fetch_Expired requests for warning data
 * On page update removes all cached queries that are now out of date from request Arr
 * Checks requestSet if request is out of data, is so the request is filtered out
 * Checks if request is of init type and already exists if so request is filtered out
 * Remove any refetch requests as these are now out of date based on layout update
 */
export default function* prioritiseWeatherWarningsForLocation(): SagaIterator {
  const buffer: Buffer<WEATHER_WARNINGS_FETCH_TYPE> = buffers.expanding();

  /** Match redux requests REDUX_WEATHER_WARNINGS_FETCH_INIT,REDUX_WEATHER_WARNINGS_FETCH_RETRY,REDUX_WEATHER_WARNINGS_FETCH_EXPIRED  */
  const channelAction = (action: { type: weatherWarningsFetchTypes }) => {
    const reduxWarningsFetchTypeInit = REDUX_WEATHER_WARNINGS_FETCH_INIT({
      warningProps: { type: '', key: '' },
      timestamp: Date.now(),
    }).type;
    const reduxWarningsFetchTypeRetry = REDUX_WEATHER_WARNINGS_FETCH_RETRY({
      warningProps: { type: '', key: '' },
      timestamp: Date.now(),
    }).type;
    const reduxWarningsFetchTypeExpired = REDUX_WEATHER_WARNINGS_FETCH_EXPIRED({
      warningProps: { type: '', key: '' },
      timestamp: Date.now(),
    }).type;

    const matches = [reduxWarningsFetchTypeInit, reduxWarningsFetchTypeRetry, reduxWarningsFetchTypeExpired];
    if (matches.indexOf(action.type) >= 0) {
      return true;
    }
    return false;
  };

  const channel: Channel<WEATHER_WARNINGS_FETCH_TYPE> = yield actionChannel(channelAction as ActionPattern, buffer);

  const requestSet = WarningRequestArray();

  while (true) {
    // we need to use `yield take` as that triggers the process
    const action: WEATHER_WARNINGS_FETCH_TYPE<weatherWarningsFetchTypes> = yield take(channel);

    let actions: Array<WEATHER_WARNINGS_FETCH_TYPE> = [action];
    if (!buffer.isEmpty()) {
      const otherActions = yield flush(channel);
      // merge actions with those in the queue
      actions = actions.concat(otherActions);
    }
    const pageUpdateTS: number = yield select((state: IRepoState) => state.LAYOUT.pageTimestamp);
    requestSet.updatePageLoadTime(pageUpdateTS);
    const toRunActions = actions.filter((a) => {
      const requestString = `${a.payload.warningProps.type}${a.payload.warningProps.key}`;
      return requestSet.addPageRequest(a.payload.timestamp, requestString, action.type);
    });
    yield all(toRunActions.map((a) => fork(updateWarningsForLocation, a)));
  }
}
